/**
 * Which assignments have been waiting too long for their mp session.
 *
 * `assign_orientation` inserts the progress row at `session_id = 0` and a
 * scheduled procedure fills it in, normally within seconds, retrying with
 * backoff when mp refuses. An assignment still at 0 long after it was assigned
 * means every retry is failing, which is almost always configuration: the
 * org's `mp_service_config` token expired, or lost its mp Writer grant. The
 * trainee only ever sees "still being provisioned", so the admin banner is
 * where it surfaces.
 */

/** How long an assignment may sit without a session before it counts as stuck.
 *  The module's retries by then: 30 s, 1, 2 and 4 min. */
export const STUCK_AFTER_MS = 10 * 60 * 1000;

export type ProvisioningRow = {
  assignmentId: bigint;
  status: { tag: string };
  assignedAtMs: number;
};

/** The rows still waiting for a session after `STUCK_AFTER_MS`. A row with no
 *  progress pointer counts as waiting (the progress row is inserted in the same
 *  reducer, so a missing one is a view lag, and lag clears long before 10 min). */
export function stuckAssignments<T extends ProvisioningRow>(
  rows: T[],
  sessionIdByAssignment: Map<bigint, bigint>,
  nowMs: number,
): T[] {
  return rows.filter(
    (r) =>
      (r.status.tag === 'NotStarted' || r.status.tag === 'InProgress') &&
      (sessionIdByAssignment.get(r.assignmentId) ?? 0n) === 0n &&
      nowMs - r.assignedAtMs > STUCK_AFTER_MS,
  );
}
